const Ticker = (function TickerFactory(window) {
  'use strict'

  const Ticker = {}
  Ticker.addListener = function addListener(callback) {
    if (typeof callback !== 'function')
      throw 'Ticker.addListener() requires a function reference passed for a callback.'
    listeners.push(callback)
    if (!started) {
      started = true
      queueFrame()
    }
  }

  let started = false
  let lastTimestamp = 0
  let listeners = []

  function queueFrame() {
    if (window.requestAnimationFrame) {
      requestAnimationFrame(frameHandler)
    } else {
      webkitRequestAnimationFrame(frameHandler)
    }
  }

  function frameHandler(timestamp) {
    let frameTime = timestamp - lastTimestamp
    lastTimestamp = timestamp
    if (frameTime < 0) {
      frameTime = 17
    } else if (frameTime > 68) {
      frameTime = 68
    }

    listeners.forEach((listener) =>
      listener.call(window, frameTime, frameTime / 16.6667)
    )
    queueFrame()
  }

  return Ticker
})(window)

const Stage = (function StageFactory(window, document, Ticker) {
  'use strict'

  let lastTouchTimestamp = 0

  function Stage(canvas) {
    if (typeof canvas === 'string') canvas = document.getElementById(canvas)

    this.canvas = canvas
    this.ctx = canvas.getContext('2d')

    this.canvas.style.touchAction = 'none'

    this.speed = 1

    this.dpr = Stage.disableHighDPI
      ? 1
      : (window.devicePixelRatio || 1) / (this.ctx.backingStorePixelRatio || 1)

    this.width = canvas.width
    this.height = canvas.height
    this.naturalWidth = this.width * this.dpr
    this.naturalHeight = this.height * this.dpr

    if (this.width !== this.naturalWidth) {
      this.canvas.width = this.naturalWidth
      this.canvas.height = this.naturalHeight
      this.canvas.style.width = this.width + 'px'
      this.canvas.style.height = this.height + 'px'
    }
    Stage.stages.push(this)
    this._listeners = {
      resize: [],
      pointerstart: [],
      pointermove: [],
      pointerend: [],
      lastPointerPos: { x: 0, y: 0 },
    }
  }

  Stage.stages = []
  Stage.disableHighDPI = false

  Stage.prototype.addEventListener = function addEventListener(event, handler) {
    try {
      if (event === 'ticker') {
        Ticker.addListener(handler)
      } else {
        this._listeners[event].push(handler)
      }
    } catch (e) {
      throw 'Invalid Event'
    }
  }

  Stage.prototype.dispatchEvent = function dispatchEvent(event, val) {
    const listeners = this._listeners[event]
    if (listeners) {
      listeners.forEach((listener) => listener.call(this, val))
    } else {
      throw 'Invalid Event'
    }
  }

  Stage.prototype.resize = function resize(w, h) {
    this.width = w
    this.height = h
    this.naturalWidth = w * this.dpr
    this.naturalHeight = h * this.dpr
    this.canvas.width = this.naturalWidth
    this.canvas.height = this.naturalHeight
    this.canvas.style.width = w + 'px'
    this.canvas.style.height = h + 'px'
    this.dispatchEvent('resize')
  }

  Stage.windowToCanvas = function windowToCanvas(canvas, x, y) {
    const bbox = canvas.getBoundingClientRect()
    return {
      x: (x - bbox.left) * (canvas.width / bbox.width),
      y: (y - bbox.top) * (canvas.height / bbox.height),
    }
  }
  Stage.mouseHandler = function mouseHandler(evt) {
    if (Date.now() - lastTouchTimestamp < 500) {
      return
    }

    let type = 'start'
    if (evt.type === 'mousemove') {
      type = 'move'
    } else if (evt.type === 'mouseup') {
      type = 'end'
    }

    Stage.stages.forEach((stage) => {
      const pos = Stage.windowToCanvas(stage.canvas, evt.clientX, evt.clientY)
      stage.pointerEvent(type, pos.x / stage.dpr, pos.y / stage.dpr)
    })
  }
  Stage.touchHandler = function touchHandler(evt) {
    lastTouchTimestamp = Date.now()
    let type = 'start'
    if (evt.type === 'touchmove') {
      type = 'move'
    } else if (evt.type === 'touchend') {
      type = 'end'
    }

    Stage.stages.forEach((stage) => {
      for (let touch of Array.from(evt.changedTouches)) {
        let pos
        if (type !== 'end') {
          pos = Stage.windowToCanvas(stage.canvas, touch.clientX, touch.clientY)
          stage._listeners.lastPointerPos = pos
          if (type === 'start')
            stage.pointerEvent('move', pos.x / stage.dpr, pos.y / stage.dpr)
        } else {
          pos = stage._listeners.lastPointerPos
        }
        stage.pointerEvent(type, pos.x / stage.dpr, pos.y / stage.dpr)
      }
    })
  }

  Stage.prototype.pointerEvent = function pointerEvent(type, x, y) {
    const evt = {
      type: type,
      x: x,
      y: y,
    }
    evt.onCanvas = x >= 0 && x <= this.width && y >= 0 && y <= this.height

    this.dispatchEvent('pointer' + type, evt)
  }

  document.addEventListener('mousedown', Stage.mouseHandler)
  document.addEventListener('mousemove', Stage.mouseHandler)
  document.addEventListener('mouseup', Stage.mouseHandler)
  document.addEventListener('touchstart', Stage.touchHandler)
  document.addEventListener('touchmove', Stage.touchHandler)
  document.addEventListener('touchend', Stage.touchHandler)

  return Stage
})(window, document, Ticker)

const MyMath = (function MyMathFactory(Math) {
  const MyMath = {}

  MyMath.toDeg = 180 / Math.PI
  MyMath.toRad = Math.PI / 180
  MyMath.halfPI = Math.PI / 2
  MyMath.twoPI = Math.PI * 2

  MyMath.dist = (width, height) => {
    return Math.sqrt(width * width + height * height)
  }

  MyMath.pointDist = (x1, y1, x2, y2) => {
    const distX = x2 - x1
    const distY = y2 - y1
    return Math.sqrt(distX * distX + distY * distY)
  }

  MyMath.angle = (width, height) => MyMath.halfPI + Math.atan2(height, width)

  MyMath.pointAngle = (x1, y1, x2, y2) =>
    MyMath.halfPI + Math.atan2(y2 - y1, x2 - x1)

  MyMath.splitVector = (speed, angle) => ({
    x: Math.sin(angle) * speed,
    y: -Math.cos(angle) * speed,
  })

  MyMath.random = (min, max) => Math.random() * (max - min) + min

  MyMath.randomInt = (min, max) => ((Math.random() * (max - min + 1)) | 0) + min

  MyMath.randomChoice = function randomChoice(choices) {
    if (arguments.length === 1 && Array.isArray(choices)) {
      return choices[(Math.random() * choices.length) | 0]
    }
    return arguments[(Math.random() * arguments.length) | 0]
  }

  MyMath.clamp = function clamp(num, min, max) {
    return Math.min(Math.max(num, min), max)
  }
  return MyMath
})(Math)

;('use strict')

const IS_MOBILE = window.innerWidth <= 640
const IS_DESKTOP = window.innerWidth > 800
const IS_HEADER = IS_DESKTOP && window.innerHeight < 300
const IS_HIGH_END_DEVICE = (() => {
  const hwConcurrency = navigator.hardwareConcurrency
  if (!hwConcurrency) {
    return false
  }

  const minCount = window.innerWidth <= 1024 ? 4 : 8
  return hwConcurrency >= minCount
})()

const MAX_WIDTH = 7680
const MAX_HEIGHT = 4320
const GRAVITY = 0.9
let simSpeed = 1

function getDefaultScaleFactor() {
  if (IS_MOBILE) return 0.9
  if (IS_HEADER) return 0.75
  return 1
}

let stageW, stageH
let quality = 1
let isLowQuality = false
let isNormalQuality = true
let isHighQuality = false
let isFirst = true
const QUALITY_LOW = 1
const QUALITY_NORMAL = 2
const QUALITY_HIGH = 3
const SKY_LIGHT_NONE = 0
const SKY_LIGHT_DIM = 1
const COLOR = {
  Gold: '#e0004d',
  White: '#ffffff',
}
const INVISIBLE = '_INVISIBLE_'
const PI_2 = Math.PI * 2
const PI_HALF = Math.PI * 0.5
const trailsStage = new Stage('trails-canvas')
const mainStage = new Stage('main-canvas')
const stages = [trailsStage, mainStage]
const store = {
  _listeners: new Set(),
  _dispatch(prevState) {
    this._listeners.forEach((listener) => listener(this.state, prevState))
  },
  state: {
    config: {
      quality: String(IS_HIGH_END_DEVICE ? QUALITY_HIGH : QUALITY_NORMAL),
      shell: 'Crackle',
      size: IS_DESKTOP ? '3' : IS_MOBILE ? '.2' : IS_HEADER ? '1.2' : '2',
      autoLaunch: true,
      finale: false,
      longExposure: false,
      scaleFactor: getDefaultScaleFactor(),
      centerImage: 'https://cdn.shopify.com/s/files/1/0656/9079/6273/files/svg.png',
    },
  },
  setState(nextState) {
    const prevState = this.state
    this.state = Object.assign({}, this.state, nextState)
    this._dispatch(prevState)
  },

  subscribe(listener) {
    this._listeners.add(listener)
    return () => this._listeners.remove(listener)
  },
}

function configDidUpdate() {
  quality = qualitySelector()
  isLowQuality = quality === QUALITY_LOW
  isNormalQuality = quality === QUALITY_NORMAL
  isHighQuality = quality === QUALITY_HIGH
  Spark.drawWidth = quality === QUALITY_HIGH ? 0.75 : 1
}

const soundEnabledSelector = (state = store.state) => state.soundEnabled
const qualitySelector = () => +store.state.config.quality
const shellNameSelector = () => store.state.config.shell
const shellSizeSelector = () => +store.state.config.size
const finaleSelector = () => store.state.config.finale
// const skyLightingSelector = () => +store.state.config.skyLighting
const scaleFactorSelector = () => store.state.config.scaleFactor

const appNodes = {
  stageContainer: '.stage-container',
}

Object.keys(appNodes).forEach((key) => {
  appNodes[key] = document.querySelector(appNodes[key])
})

const COLOR_NAMES = Object.keys(COLOR)
const COLOR_CODES = COLOR_NAMES.map((colorName) => COLOR[colorName])
const COLOR_CODES_W_INVIS = [...COLOR_CODES, INVISIBLE]
const COLOR_CODE_INDEXES = COLOR_CODES_W_INVIS.reduce((obj, code, i) => {
  obj[code] = i
  return obj
}, {})
const COLOR_TUPLES = {}
COLOR_CODES.forEach((hex) => {
  COLOR_TUPLES[hex] = {
    r: parseInt(hex.substr(1, 2), 16),
    g: parseInt(hex.substr(3, 2), 16),
    b: parseInt(hex.substr(5, 2), 16),
  }
})

function randomColorSimple() {
  return COLOR_CODES[(Math.random() * COLOR_CODES.length) | 0]
}

let lastColor
function randomColor(options) {
  const notSame = options && options.notSame
  const notColor = options && options.notColor
  const limitWhite = options && options.limitWhite
  let color = randomColorSimple()
  let attempts = 0
  const MAX_ATTEMPTS = 10 // 防止无限循环

  if (limitWhite && color === COLOR.White && Math.random() < 0.6) {
    color = randomColorSimple()
  }

  if (notSame) {
    while (color === lastColor && attempts < MAX_ATTEMPTS) {
      color = randomColorSimple()
      attempts++
    }
  } else if (notColor) {
    while (color === notColor && attempts < MAX_ATTEMPTS) {
      color = randomColorSimple()
      attempts++
    }
  }

  lastColor = color
  return color
}

function whiteOrGold() {
  return Math.random() < 0.5 ? COLOR.Gold : COLOR.White
}

function makePistilColor(shellColor) {
  return shellColor === COLOR.White || shellColor === COLOR.Gold
    ? randomColor({ notColor: shellColor })
    : whiteOrGold()
}

const crysanthemumShell = (size = 1) => {
  const glitter = Math.random() < 0.25
  const singleColor = Math.random() < 0.72
  const color = singleColor
    ? randomColor({ limitWhite: true })
    : [randomColor(), randomColor({ notSame: true })]
  const pistil = singleColor && Math.random() < 0.42
  const pistilColor = pistil && makePistilColor(color)
  const secondColor =
    singleColor && (Math.random() < 0.2 || color === COLOR.White)
      ? pistilColor || randomColor({ notColor: color, limitWhite: true })
      : null
  const streamers = !pistil && color !== COLOR.White && Math.random() < 0.42
  let starDensity = glitter ? 1.1 : 1.25
  if (isLowQuality) starDensity *= 0.8
  if (isHighQuality) starDensity = 1.2
  return {
    shellSize: size,
    spreadSize: 300 + size * 100,
    starLife: 900 + size * 200,
    starDensity,
    color,
    secondColor,
    glitter: glitter ? 'light' : '',
    glitterColor: whiteOrGold(),
    pistil,
    pistilColor,
    streamers,
  }
}

const crackleShell = (size = 1) => {
  const color = Math.random() < 0.75 ? COLOR.Gold : randomColor()
  return {
    shellSize: size,
    spreadSize: 380 + size * 75,
    starDensity: isLowQuality ? 0.65 : 1,
    starLife: 600 + size * 100,
    starLifeVariation: 0.32,
    glitter: 'light',
    glitterColor: COLOR.Gold,
    color,
    crackle: true,
    pistil: Math.random() < 0.65,
    pistilColor: makePistilColor(color),
  }
}

function shellFromConfig(size) {
  return shellTypes[shellNameSelector()](size)
}

function randomFastShell() {
  return shellTypes[shellNameSelector()]
}

const shellTypes = {
  Crackle: crackleShell,
}

const shellNames = Object.keys(shellTypes)

function fitShellPositionInBoundsH(position) {
  const edge = 0.18
  return (1 - edge * 2) * position + edge
}

function fitShellPositionInBoundsV(position) {
  return position * 0.75
}

function getRandomShellPositionH() {
  return fitShellPositionInBoundsH(Math.random())
}

function getRandomShellPositionV() {
  return fitShellPositionInBoundsV(Math.random())
}

function getRandomShellSize() {
  const baseSize = shellSizeSelector()
  const maxVariance = Math.min(2.5, baseSize)
  const variance = Math.random() * maxVariance
  const size = baseSize - variance
  const height = maxVariance === 0 ? Math.random() : 1 - variance / maxVariance
  const centerOffset = Math.random() * (1 - height * 0.65) * 0.5
  const x = Math.random() < 0.5 ? 0.5 - centerOffset : 0.5 + centerOffset
  return {
    size,
    x: fitShellPositionInBoundsH(x),
    height: fitShellPositionInBoundsV(height),
  }
}

function seqRandomShell() {
  const size = getRandomShellSize()
  const shell = new Shell(shellFromConfig(size.size))
  shell.launch(size.x, size.height)

  let extraDelay = shell.starLife
  if (shell.fallingLeaves) {
    extraDelay = 4600
  }

  return 900 + Math.random() * 600 + extraDelay
}

function seqRandomFastShell() {
  const shellType = randomFastShell()
  const size = getRandomShellSize()
  const shell = new Shell(shellType(size.size))
  shell.launch(size.x, size.height)

  let extraDelay = shell.starLife

  return 900 + Math.random() * 600 + extraDelay
}

function seqTwoRandom() {
  const size1 = getRandomShellSize()
  const size2 = getRandomShellSize()
  const shell1 = new Shell(shellFromConfig(size1.size))
  const shell2 = new Shell(shellFromConfig(size2.size))
  const leftOffset = Math.random() * 0.2 - 0.1
  const rightOffset = Math.random() * 0.2 - 0.1
  shell1.launch(0.3 + leftOffset, size1.height)
  setTimeout(() => {
    shell2.launch(0.7 + rightOffset, size2.height)
  }, 100)

  let extraDelay = Math.max(shell1.starLife, shell2.starLife)
  if (shell1.fallingLeaves || shell2.fallingLeaves) {
    extraDelay = 4600
  }

  return 900 + Math.random() * 600 + extraDelay
}

function seqTriple() {
  const shellType = randomFastShell()
  const baseSize = shellSizeSelector()
  const smallSize = Math.max(0, baseSize - 1.25)

  const offset = Math.random() * 0.08 - 0.04
  const shell1 = new Shell(shellType(baseSize))
  shell1.launch(0.5 + offset, 0.7)

  const leftDelay = 1000 + Math.random() * 400
  const rightDelay = 1000 + Math.random() * 400

  setTimeout(() => {
    const offset = Math.random() * 0.08 - 0.04
    const shell2 = new Shell(shellType(smallSize))
    shell2.launch(0.2 + offset, 0.1)
  }, leftDelay)

  setTimeout(() => {
    const offset = Math.random() * 0.08 - 0.04
    const shell3 = new Shell(shellType(smallSize))
    shell3.launch(0.8 + offset, 0.1)
  }, rightDelay)

  return 4000
}

function seqPyramid() {
  const barrageCountHalf = IS_DESKTOP ? 7 : 4
  const largeSize = shellSizeSelector()
  const smallSize = Math.max(0, largeSize - 3)

  function launchShell(x, useSpecial) {
    let shellType = shellTypes[shellNameSelector()]
    const shell = new Shell(shellType(useSpecial ? largeSize : smallSize))
    const height = x <= 0.5 ? x / 0.5 : (1 - x) / 0.5
    shell.launch(x, useSpecial ? 0.75 : height * 0.42)
  }

  let count = 0
  let delay = 0
  while (count <= barrageCountHalf) {
    if (count === barrageCountHalf) {
      setTimeout(() => {
        launchShell(0.5, true)
      }, delay)
    } else {
      const offset = (count / barrageCountHalf) * 0.5
      const delayOffset = Math.random() * 30 + 30
      setTimeout(() => {
        launchShell(offset, false)
      }, delay)
      setTimeout(() => {
        launchShell(1 - offset, false)
      }, delay + delayOffset)
    }

    count++
    delay += 200
  }

  return 3400 + barrageCountHalf * 250
}

function seqSmallBarrage() {
  seqSmallBarrage.lastCalled = Date.now()
  const barrageCount = IS_DESKTOP ? 11 : 5
  const specialIndex = IS_DESKTOP ? 3 : 1
  const shellSize = Math.max(0, shellSizeSelector() - 2)

  function launchShell(x, useSpecial) {
    let shellType = shellTypes[shellNameSelector()]
    const shell = new Shell(shellType(shellSize))
    const height = (Math.cos(x * 5 * Math.PI + PI_HALF) + 1) / 2
    shell.launch(x, height * 0.75)
  }

  let count = 0
  let delay = 0
  while (count < barrageCount) {
    if (count === 0) {
      launchShell(0.5, false)
      count += 1
    } else {
      const offset = (count + 1) / barrageCount / 2
      const delayOffset = Math.random() * 30 + 30
      const useSpecial = count === specialIndex
      setTimeout(() => {
        launchShell(0.5 + offset, useSpecial)
      }, delay)
      setTimeout(() => {
        launchShell(0.5 - offset, useSpecial)
      }, delay + delayOffset)
      count += 2
    }
    delay += 200
  }

  return 3400 + barrageCount * 120
}
seqSmallBarrage.cooldown = 15000
seqSmallBarrage.lastCalled = Date.now()

let isFirstSeq = true
const finaleCount = 32
let currentFinaleCount = 0
function startSequence() {
  if (isFirstSeq) {
    isFirstSeq = false
    if (IS_HEADER) {
      // return seqTwoRandom()
      return seqTriple()
    } else {
      if (isFirst) {
        isFirst = false
        return seqTriple()
      }
      const shell = new Shell(crysanthemumShell(shellSizeSelector()))
      shell.launch(0.5, 0.5)
      return 2400
    }
  }

  if (finaleSelector()) {
    seqRandomFastShell()
    if (currentFinaleCount < finaleCount) {
      currentFinaleCount++
      return 170
    } else {
      currentFinaleCount = 0
      return 6000
    }
  }

  const rand = Math.random()

  if (
    rand < 0.08 &&
    Date.now() - seqSmallBarrage.lastCalled > seqSmallBarrage.cooldown
  ) {
    return seqTriple()
  }

  if (rand < 0.1) {
    return seqRandomShell()
  }

  if (rand < 0.6 && !IS_HEADER) {
    return seqRandomShell()
  } else if (rand < 0.8) {
    return seqTwoRandom()
  } else if (rand < 1) {
    return seqTriple()
  }
}

let isUpdatingSpeed = false

function handleResize() {
  const w = appNodes.stageContainer.offsetWidth
  const h = appNodes.stageContainer.offsetHeight
  // const containerW = Math.min(w, MAX_WIDTH)
  // const containerH = w <= 420 ? h : Math.min(h, MAX_HEIGHT)
  const containerW = w
  const containerH = h
  // appNodes.stageContainer.style.width = containerW + 'px'
  // appNodes.stageContainer.style.height = containerH + 'px'
  stages.forEach((stage) => stage.resize(containerW, containerH))
  const scaleFactor = scaleFactorSelector()
  stageW = containerW / scaleFactor
  stageH = containerH / scaleFactor
}

handleResize()

window.addEventListener('resize', handleResize)

let currentFrame = 0
let speedBarOpacity = 0
let autoLaunchTime = 0

function updateGlobals(timeStep, lag) {
  currentFrame++

  if (!isUpdatingSpeed) {
    speedBarOpacity -= lag / 30
    if (speedBarOpacity < 0) {
      speedBarOpacity = 0
    }
  }

  if (store.state.config.autoLaunch) {
    autoLaunchTime -= timeStep
    if (autoLaunchTime <= 0) {
      autoLaunchTime = startSequence() * 1.25
    }
  }
}

function update(frameTime, lag) {
  const timeStep = frameTime * simSpeed
  const speed = simSpeed * lag

  updateGlobals(timeStep, lag)

  const starDrag = 1 - (1 - Star.airDrag) * speed
  const starDragHeavy = 1 - (1 - Star.airDragHeavy) * speed
  const sparkDrag = 1 - (1 - Spark.airDrag) * speed
  const gAcc = (timeStep / 1000) * GRAVITY
  COLOR_CODES_W_INVIS.forEach((color) => {
    // Stars
    const stars = Star.active[color]
    for (let i = stars.length - 1; i >= 0; i = i - 1) {
      const star = stars[i]
      if (star.updateFrame === currentFrame) {
        continue
      }
      star.updateFrame = currentFrame

      star.life -= timeStep
      if (star.life <= 0) {
        stars.splice(i, 1)
        Star.returnInstance(star)
      } else {
        const burnRate = Math.pow(star.life / star.fullLife, 0.5)
        const burnRateInverse = 1 - burnRate

        star.prevX = star.x
        star.prevY = star.y
        star.x += star.speedX * speed
        star.y += star.speedY * speed
        if (!star.heavy) {
          star.speedX *= starDrag
          star.speedY *= starDrag
        } else {
          star.speedX *= starDragHeavy
          star.speedY *= starDragHeavy
        }
        star.speedY += gAcc

        if (star.spinRadius) {
          star.spinAngle += star.spinSpeed * speed
          star.x += Math.sin(star.spinAngle) * star.spinRadius * speed
          star.y += Math.cos(star.spinAngle) * star.spinRadius * speed
        }

        if (star.sparkFreq) {
          star.sparkTimer -= timeStep
          while (star.sparkTimer < 0) {
            star.sparkTimer +=
              star.sparkFreq * 0.75 + star.sparkFreq * burnRateInverse * 4
            Spark.add(
              star.x,
              star.y,
              star.sparkColor,
              Math.random() * PI_2,
              Math.random() * star.sparkSpeed * burnRate,
              star.sparkLife * 0.8 +
                Math.random() * star.sparkLifeVariation * star.sparkLife
            )
          }
        }

        if (star.life < star.transitionTime) {
          if (star.secondColor && !star.colorChanged) {
            star.colorChanged = true
            star.color = star.secondColor
            stars.splice(i, 1)
            Star.active[star.secondColor].push(star)
            if (star.secondColor === INVISIBLE) {
              star.sparkFreq = 0
            }
          }

          if (star.strobe) {
            star.visible = Math.floor(star.life / star.strobeFreq) % 3 === 0
          }
        }
      }
    }

    // Sparks
    const sparks = Spark.active[color]
    for (let i = sparks.length - 1; i >= 0; i = i - 1) {
      const spark = sparks[i]
      spark.life -= timeStep
      if (spark.life <= 0) {
        sparks.splice(i, 1)
        Spark.returnInstance(spark)
      } else {
        spark.prevX = spark.x
        spark.prevY = spark.y
        spark.x += spark.speedX * speed
        spark.y += spark.speedY * speed
        spark.speedX *= sparkDrag
        spark.speedY *= sparkDrag
        spark.speedY += gAcc
      }
    }
  })

  render(speed)
}

function render(speed) {
  const { dpr } = mainStage
  const width = stageW
  const height = stageH
  const trailsCtx = trailsStage.ctx
  const mainCtx = mainStage.ctx

  const scaleFactor = scaleFactorSelector()
  trailsCtx.scale(dpr * scaleFactor, dpr * scaleFactor)
  mainCtx.scale(dpr * scaleFactor, dpr * scaleFactor)
  trailsCtx.globalCompositeOperation = 'source-over'
  trailsCtx.fillStyle = `rgba(0, 0, 0, ${
    store.state.config.longExposure ? 0.0025 : 0.175 * speed
  })`
  trailsCtx.fillRect(0, 0, width, height)
  mainCtx.clearRect(0, 0, width, height)

  while (BurstFlash.active.length) {
    const bf = BurstFlash.active.pop()
    const burstGradient = trailsCtx.createRadialGradient(
      bf.x,
      bf.y,
      0,
      bf.x,
      bf.y,
      bf.radius
    )
    burstGradient.addColorStop(0.024, 'rgba(255, 255, 255, 1)')
    burstGradient.addColorStop(0.125, 'rgba(255, 160, 20, 0.2)')
    burstGradient.addColorStop(0.32, 'rgba(255, 140, 20, 0.11)')
    burstGradient.addColorStop(1, 'rgba(255, 120, 20, 0)')
    trailsCtx.fillStyle = burstGradient
    trailsCtx.fillRect(
      bf.x - bf.radius,
      bf.y - bf.radius,
      bf.radius * 2,
      bf.radius * 2
    )

    BurstFlash.returnInstance(bf)
  }
  trailsCtx.globalCompositeOperation = 'lighten'

  // Draw stars
  trailsCtx.lineWidth = Star.drawWidth
  trailsCtx.lineCap = isLowQuality ? 'square' : 'round'
  mainCtx.strokeStyle = '#fff'
  mainCtx.lineWidth = 1
  mainCtx.beginPath()
  COLOR_CODES.forEach((color) => {
    const stars = Star.active[color]
    trailsCtx.strokeStyle = color
    trailsCtx.beginPath()
    stars.forEach((star) => {
      if (star.visible) {
        trailsCtx.moveTo(star.x, star.y)
        trailsCtx.lineTo(star.prevX, star.prevY)
        mainCtx.moveTo(star.x, star.y)
        mainCtx.lineTo(star.x - star.speedX * 1.6, star.y - star.speedY * 1.6)
      }
    })
    trailsCtx.stroke()
  })
  mainCtx.stroke()

  // Draw sparks
  trailsCtx.lineWidth = Spark.drawWidth
  trailsCtx.lineCap = 'butt'
  COLOR_CODES.forEach((color) => {
    const sparks = Spark.active[color]
    trailsCtx.strokeStyle = color
    trailsCtx.beginPath()
    sparks.forEach((spark) => {
      trailsCtx.moveTo(spark.x, spark.y)
      trailsCtx.lineTo(spark.prevX, spark.prevY)
    })
    trailsCtx.stroke()
  })

  if (speedBarOpacity) {
    const speedBarHeight = 6
    mainCtx.globalAlpha = speedBarOpacity
    mainCtx.fillRect(
      0,
      height - speedBarHeight,
      width * simSpeed,
      speedBarHeight
    )
    mainCtx.globalAlpha = 1
  }

  let bNum = IS_MOBILE ? 0.7 : 1
  // 渲染中心图片效果
  Shell.centerParticles.forEach((particle, index) => {
    const ctx = mainStage.ctx
    particle.life -= speed * 16 // 减少生命周期
    // 检查粒子是否应该移除
    if (particle.life <= 0 || particle.opacity <= 0 || particle.scale > 2) {
      Shell.centerParticles.splice(index, 1) // 移除过期粒子
      return
    }

    particle.scale += 0.01 * speed // 图片逐渐放大
    particle.opacity -= 0.01 * speed // 图片逐渐透明
    ctx.save()
    ctx.globalAlpha = particle.opacity
    ctx.translate(particle.x, particle.y)
    ctx.scale(particle.scale, particle.scale)
    ctx.drawImage(
      particle.image,
      -150 * bNum,
      -39 * bNum,
      300 * bNum,
      78 * bNum
    ) // 图片以中心点为基准
    ctx.restore()
  })

  trailsCtx.setTransform(1, 0, 0, 1, 0, 0)
  mainCtx.setTransform(1, 0, 0, 1, 0, 0)
}

mainStage.addEventListener('ticker', update)

function createParticleArc(
  start,
  arcLength,
  count,
  randomness,
  particleFactory
) {
  const angleDelta = arcLength / count
  const end = start + arcLength - angleDelta * 0.5

  if (end > start) {
    for (let angle = start; angle < end; angle = angle + angleDelta) {
      particleFactory(angle + Math.random() * angleDelta * randomness)
    }
  } else {
    for (let angle = start; angle > end; angle = angle + angleDelta) {
      particleFactory(angle + Math.random() * angleDelta * randomness)
    }
  }
}

function createBurst(count, particleFactory, startAngle = 0, arcLength = PI_2) {
  const R = 0.5 * Math.sqrt(count / Math.PI)
  const C = 2 * R * Math.PI
  const C_HALF = C / 2

  for (let i = 0; i <= C_HALF; i++) {
    const ringAngle = (i / C_HALF) * PI_HALF
    const ringSize = Math.cos(ringAngle)
    const partsPerFullRing = C * ringSize
    const partsPerArc = partsPerFullRing * (arcLength / PI_2)

    const angleInc = PI_2 / partsPerFullRing
    const angleOffset = Math.random() * angleInc + startAngle
    const maxRandomAngleOffset = angleInc * 0.33

    for (let i = 0; i < partsPerArc; i++) {
      const randomAngleOffset = Math.random() * maxRandomAngleOffset
      let angle = angleInc * i + angleOffset + randomAngleOffset
      particleFactory(angle, ringSize)
    }
  }
}

function crossetteEffect(star) {
  const startAngle = Math.random() * PI_HALF
  createParticleArc(startAngle, PI_2, 4, 0.5, (angle) => {
    Star.add(star.x, star.y, star.color, angle, Math.random() * 0.6 + 0.75, 600)
  })
}

function floralEffect(star) {
  const count = 12 + 6 * quality
  createBurst(count, (angle, speedMult) => {
    Star.add(
      star.x,
      star.y,
      star.color,
      angle,
      speedMult * 2.4,
      1000 + Math.random() * 300,
      star.speedX,
      star.speedY
    )
  })
  BurstFlash.add(star.x, star.y, 46)
}

function fallingLeavesEffect(star) {
  createBurst(7, (angle, speedMult) => {
    const newStar = Star.add(
      star.x,
      star.y,
      INVISIBLE,
      angle,
      speedMult * 2.4,
      2400 + Math.random() * 600,
      star.speedX,
      star.speedY
    )

    newStar.sparkColor = COLOR.Gold
    newStar.sparkFreq = 144 / quality
    newStar.sparkSpeed = 0.28
    newStar.sparkLife = 750
    newStar.sparkLifeVariation = 3.2
  })

  BurstFlash.add(star.x, star.y, 46)
}

function crackleEffect(star) {
  const count = isHighQuality ? 32 : 16
  createParticleArc(0, PI_2, count, 1.8, (angle) => {
    Spark.add(
      star.x,
      star.y,
      COLOR.Gold,
      angle,
      Math.pow(Math.random(), 0.45) * 2.4,
      300 + Math.random() * 200
    )
  })
}

class Shell {
  constructor(options) {
    Object.assign(this, options)
    this.starLifeVariation = options.starLifeVariation || 0.125
    this.color = options.color || randomColor()
    this.glitterColor = options.glitterColor || this.color

    if (!this.starCount) {
      const density = options.starDensity || 1
      const scaledSize = this.spreadSize / 54
      this.starCount = Math.max(6, scaledSize * scaledSize * density)
    }
  }

  launch(position, launchHeight) {
    const width = stageW
    const height = stageH
    const hpad = 60
    const vpad = 50
    const minHeightPercent = 0.45

    const minHeight = height - height * minHeightPercent

    const launchX = position * (width - hpad * 2) + hpad
    const launchY = height
    const burstY = minHeight - launchHeight * (minHeight - vpad)

    const launchDistance = launchY - burstY
    const launchVelocity = Math.pow(launchDistance * 0.04, 0.64)

    const comet = (this.comet = Star.add(
      launchX,
      launchY,
      typeof this.color === 'string' && this.color !== 'random'
        ? this.color
        : COLOR.White,
      Math.PI,
      launchVelocity * (this.horsetail ? 1.2 : 1),
      launchVelocity * (this.horsetail ? 100 : 400)
    ))

    comet.heavy = true
    comet.spinRadius = MyMath.random(0.32, 0.85)
    comet.sparkFreq = 32 / quality
    if (isHighQuality) comet.sparkFreq = 8
    comet.sparkLife = 320
    comet.sparkLifeVariation = 3
    if (this.glitter === 'willow' || this.fallingLeaves) {
      comet.sparkFreq = 20 / quality
      comet.sparkSpeed = 0.5
      comet.sparkLife = 500
    }
    if (this.color === INVISIBLE) {
      comet.sparkColor = COLOR.Gold
    }

    if (Math.random() > 0.4 && !this.horsetail) {
      comet.secondColor = INVISIBLE
      comet.transitionTime = Math.pow(Math.random(), 1.5) * 700 + 500
    }

    comet.onDeath = (comet) => this.burst(comet.x, comet.y)
  }

  burst(x, y) {
    const speed = this.spreadSize / 96
    let color, onDeath, sparkFreq, sparkSpeed, sparkLife
    let sparkLifeVariation = 0.25

    if (this.crossette)
      onDeath = (star) => {
        crossetteEffect(star)
      }
    if (this.crackle)
      onDeath = (star) => {
        crackleEffect(star)
      }
    if (this.floral) onDeath = floralEffect
    if (this.fallingLeaves) onDeath = fallingLeavesEffect

    if (this.glitter === 'light') {
      sparkFreq = 400
      sparkSpeed = 0.3
      sparkLife = 300
      sparkLifeVariation = 2
    } else if (this.glitter === 'medium') {
      sparkFreq = 200
      sparkSpeed = 0.44
      sparkLife = 700
      sparkLifeVariation = 2
    } else if (this.glitter === 'heavy') {
      sparkFreq = 80
      sparkSpeed = 0.8
      sparkLife = 1400
      sparkLifeVariation = 2
    } else if (this.glitter === 'thick') {
      sparkFreq = 16
      sparkSpeed = isHighQuality ? 1.65 : 1.5
      sparkLife = 1400
      sparkLifeVariation = 3
    } else if (this.glitter === 'streamer') {
      sparkFreq = 32
      sparkSpeed = 1.05
      sparkLife = 620
      sparkLifeVariation = 2
    } else if (this.glitter === 'willow') {
      sparkFreq = 120
      sparkSpeed = 0.34
      sparkLife = 1400
      sparkLifeVariation = 3.8
    }

    sparkFreq = sparkFreq / quality
    const starFactory = (angle, speedMult) => {
      const standardInitialSpeed = this.spreadSize / 1800
      const star = Star.add(
        x,
        y,
        color || randomColor(),
        angle,
        speedMult * speed,
        this.starLife + Math.random() * this.starLife * this.starLifeVariation,
        this.horsetail ? this.comet && this.comet.speedX : 0,
        this.horsetail ? this.comet && this.comet.speedY : -standardInitialSpeed
      )

      if (this.secondColor) {
        star.transitionTime = this.starLife * (Math.random() * 0.05 + 0.32)
        star.secondColor = this.secondColor
      }

      if (this.strobe) {
        star.transitionTime = this.starLife * (Math.random() * 0.08 + 0.46)
        star.strobe = true
        star.strobeFreq = Math.random() * 20 + 40
        if (this.strobeColor) {
          star.secondColor = this.strobeColor
        }
      }

      star.onDeath = onDeath

      if (this.glitter) {
        star.sparkFreq = sparkFreq
        star.sparkSpeed = sparkSpeed
        star.sparkLife = sparkLife
        star.sparkLifeVariation = sparkLifeVariation
        star.sparkColor = this.glitterColor
        star.sparkTimer = Math.random() * star.sparkFreq
      }
    }

    if (typeof this.color === 'string') {
      if (this.color === 'random') {
        color = null
      } else {
        color = this.color
      }

      if (this.ring) {
        const ringStartAngle = Math.random() * Math.PI
        const ringSquash = Math.pow(Math.random(), 2) * 0.85 + 0.15

        createParticleArc(0, PI_2, this.starCount, 0, (angle) => {
          const initSpeedX = Math.sin(angle) * speed * ringSquash
          const initSpeedY = Math.cos(angle) * speed
          const newSpeed = MyMath.pointDist(0, 0, initSpeedX, initSpeedY)
          const newAngle =
            MyMath.pointAngle(0, 0, initSpeedX, initSpeedY) + ringStartAngle
          const star = Star.add(
            x,
            y,
            color,
            newAngle,
            newSpeed,
            this.starLife +
              Math.random() * this.starLife * this.starLifeVariation
          )

          if (this.glitter) {
            star.sparkFreq = sparkFreq
            star.sparkSpeed = sparkSpeed
            star.sparkLife = sparkLife
            star.sparkLifeVariation = sparkLifeVariation
            star.sparkColor = this.glitterColor
            star.sparkTimer = Math.random() * star.sparkFreq
          }
        })
      } else {
        createBurst(this.starCount, starFactory)
      }
    } else if (Array.isArray(this.color)) {
      if (Math.random() < 0.5) {
        const start = Math.random() * Math.PI
        const start2 = start + Math.PI
        const arc = Math.PI
        color = this.color[0]
        createBurst(this.starCount, starFactory, start, arc)
        color = this.color[1]
        createBurst(this.starCount, starFactory, start2, arc)
      } else {
        color = this.color[0]
        createBurst(this.starCount / 2, starFactory)
        color = this.color[1]
        createBurst(this.starCount / 2, starFactory)
      }
    } else {
      throw new Error(
        'Invalid shell color. Expected string or array of strings, but got: ' +
          this.color
      )
    }

    if (this.pistil) {
      const innerShell = new Shell({
        spreadSize: this.spreadSize * 0.5,
        starLife: this.starLife * 0.6,
        starLifeVariation: this.starLifeVariation,
        starDensity: 1.4,
        color: this.pistilColor,
        glitter: 'light',
        glitterColor:
          this.pistilColor === COLOR.Gold ? COLOR.Gold : COLOR.White,
      })

      innerShell.burst(x, y)
    }

    if (this.streamers) {
      const innerShell = new Shell({
        spreadSize: this.spreadSize * 0.9,
        starLife: this.starLife * 0.8,
        starLifeVariation: this.starLifeVariation,
        starCount: Math.floor(Math.max(6, this.spreadSize / 45)),
        color: COLOR.White,
        glitter: 'streamer',
      })

      innerShell.burst(x, y)
    }

    BurstFlash.add(x, y, this.spreadSize / 4)
    // 添加中心图片粒子
    if (store.state.config.centerImage) {
      const centerParticle = {
        x,
        y,
        scale: 0.1, // 初始缩放
        opacity: 1, // 初始透明度
        life: 1000, // 生命周期，单位毫秒
        image: new Image(),
      }
      centerParticle.image.src = store.state.config.centerImage

      Shell.centerParticles.push(centerParticle)
    }
  }
}

// 在这里添加静态属性
Shell.centerParticles = []
const BurstFlash = {
  active: [],
  _pool: [],
  _new() {
    return {}
  },
  add(x, y, radius) {
    const instance = this._pool.pop() || this._new()
    instance.x = x
    instance.y = y
    instance.radius = radius
    this.active.push(instance)
    return instance
  },

  returnInstance(instance) {
    this._pool.push(instance)
  },
}

function createParticleCollection() {
  const collection = {}
  COLOR_CODES_W_INVIS.forEach((color) => {
    collection[color] = []
  })
  return collection
}

const Star = {
  drawWidth: 3,
  airDrag: 0.98,
  airDragHeavy: 0.992,
  active: createParticleCollection(),
  _pool: [],
  _new() {
    return {}
  },

  add(x, y, color, angle, speed, life, speedOffX, speedOffY) {
    const instance = this._pool.pop() || this._new()
    instance.visible = true
    instance.heavy = false
    instance.x = x
    instance.y = y
    instance.prevX = x
    instance.prevY = y
    instance.color = color
    instance.speedX = Math.sin(angle) * speed + (speedOffX || 0)
    instance.speedY = Math.cos(angle) * speed + (speedOffY || 0)
    instance.life = life
    instance.fullLife = life
    instance.spinAngle = Math.random() * PI_2
    instance.spinSpeed = 0.8
    instance.spinRadius = 0
    instance.sparkFreq = 0
    instance.sparkSpeed = 1
    instance.sparkTimer = 0
    instance.sparkColor = color
    instance.sparkLife = 750
    instance.sparkLifeVariation = 0.25
    instance.strobe = false
    this.active[color].push(instance)
    return instance
  },

  returnInstance(instance) {
    instance.onDeath && instance.onDeath(instance)
    instance.onDeath = null
    instance.secondColor = null
    instance.transitionTime = 0
    instance.colorChanged = false
    this._pool.push(instance)
  },
}

const Spark = {
  drawWidth: 0,
  airDrag: 0.9,
  active: createParticleCollection(),
  _pool: [],
  _new() {
    return {}
  },

  add(x, y, color, angle, speed, life) {
    const instance = this._pool.pop() || this._new()
    instance.x = x
    instance.y = y
    instance.prevX = x
    instance.prevY = y
    instance.color = color
    instance.speedX = Math.sin(angle) * speed
    instance.speedY = Math.cos(angle) * speed
    instance.life = life

    this.active[color].push(instance)
    return instance
  },

  returnInstance(instance) {
    this._pool.push(instance)
  },
}

configDidUpdate()

let lastClickTime = 0 // 上次点击时间
const clickCooldown = 800 // 冷却时间（毫秒）
// 监听点击事件
document
  .querySelector('.new-year-banner')
  .addEventListener('click', (event) => {
    const currentTime = Date.now()
    // 检查是否在冷却时间内
    if (currentTime - lastClickTime < clickCooldown) {
      console.warn('点击太频繁，请稍后再试！')
      return // 如果在冷却时间内，直接返回
    }

    lastClickTime = currentTime // 更新上次点击时间
    // 获取点击位置的坐标
    const { clientX, clientY } = event

    // 将点击位置转换为 Canvas 坐标
    const pos = Stage.windowToCanvas(mainStage.canvas, clientX, clientY)

    // 触发快速烟花效果
    launchQuickFirework(pos.x / mainStage.dpr, pos.y / mainStage.dpr)
  })

// 定义一个在指定位置触发快速烟花的方法
function launchQuickFirework(x, y) {
  const shellType = randomFastShell()
  const shell = new Shell(shellType(shellSizeSelector()))
  shell.launch(x / stageW, y / stageH)
}
